import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Skeleton from '../../../components/ui/Skeleton';
import employerService from '../../../utils/employerService';
import feedbackService from '../../../utils/feedbackService';
import FeedbackForm from '../../../components/feedback/FeedbackForm';
import { useAuth } from '../../../hooks/useAuth';


const QuickActions = ({ onPostJob, onSearchCandidates, onScheduleInterview, onAIMatching, onAdvancedAnalytics, onEnterpriseFeatures, onTeamManagement, onMessaging }) => {
  const [showQuickPost, setShowQuickPost] = useState(false);
  const [quickJobData, setQuickJobData] = useState({
    title: '',
    category: '',
    budget: '',
    urgency: 'normal'
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [todayOverview, setTodayOverview] = useState({
    newApplications: 0,
    interviewsScheduled: 0,
    newMessages: 0,
    activeJobs: 0
  });
  const [isLoading, setIsLoading] = useState({
    activities: true,
    overview: true,
    postingJob: false
  });
  const [postingError, setPostingError] = useState(null);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const { userProfile } = useAuth();

  useEffect(() => {
    if (userProfile?.id) {
      fetchRecentActivities();
      fetchTodayOverview();
    }
  }, [userProfile?.id]);

  const fetchRecentActivities = async () => {
    setIsLoading(prev => ({ ...prev, activities: true }));
    try {
      const result = await employerService.getRecentActivities(userProfile.id);
      if (result.success) {
        setRecentActivities(result.data);
      } else {
        console.error('Error loading recent activities:', result.error);
      }
    } catch (error) {
      console.error('Failed to fetch recent activities:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const fetchTodayOverview = async () => {
    setIsLoading(prev => ({ ...prev, overview: true }));
    try {
      const result = await employerService.getTodayOverview(userProfile.id);
      if (result.success) {
        setTodayOverview(result.data);
      } else {
        console.error('Error loading today\'s overview:', result.error);
      }
    } catch (error) {
      console.error('Failed to fetch today\'s overview:', error);
    } finally {
      setIsLoading(prev => ({ ...prev, overview: false }));
    }
  };

  // Core Actions - Most frequently used
  const coreActions = [
    {
      title: 'Post New Job',
      description: 'Create a new job posting quickly',
      icon: 'Plus',
      color: 'bg-primary-100 text-primary',
      action: () => setShowQuickPost(true),
      category: 'core'
    },
    {
      title: 'Auto Matching',
      description: 'Find perfect candidates with Auto matching',
      icon: 'Zap',
      color: 'bg-purple-100 text-purple-600',
      action: onAIMatching,
      category: 'core',
      badge: 'Match'
    },
    {
      title: 'Search Candidates',
      description: 'Browse and filter candidates',
      icon: 'Search',
      color: 'bg-accent-100 text-accent',
      action: onSearchCandidates,
      category: 'core'
    },
    {
      title: 'Messages',
      description: 'Communicate with candidates',
      icon: 'MessageSquare',
      color: 'bg-blue-100 text-blue-600',
      action: onMessaging,
      category: 'core'
    }
  ];

  // Management Actions - Team and workflow management
  const managementActions = [
    {
      title: 'Schedule Interview',
      description: 'Set up interviews with candidates',
      icon: 'Calendar',
      color: 'bg-success-100 text-success',
      action: onScheduleInterview,
      category: 'management'
    },
    {
      title: 'Team Management',
      description: 'Manage team members and roles',
      icon: 'Users',
      color: 'bg-indigo-100 text-indigo-600',
      action: onTeamManagement,
      category: 'management'
    },
    {
      title: 'Advanced Analytics',
      description: 'Deep insights and reporting',
      icon: 'BarChart3',
      color: 'bg-warning-100 text-warning',
      action: onAdvancedAnalytics,
      category: 'management'
    },
    {
      title: 'Enterprise Features',
      description: 'Integrations and automation',
      icon: 'Settings',
      color: 'bg-gray-100 text-gray-600',
      action: onEnterpriseFeatures,
      category: 'management',
      badge: 'PRO'
    }
  ];

  const jobCategories = [
    'Administrative Support',
    'Content Writing',
    'Social Media Management',
    'Customer Service',
    'Data Entry',
    'Graphic Design',
    'Web Development',
    'Digital Marketing'
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Low Priority', color: 'text-secondary' },
    { value: 'normal', label: 'Normal', color: 'text-primary' },
    { value: 'high', label: 'High Priority', color: 'text-warning' },
    { value: 'urgent', label: 'Urgent', color: 'text-error' }
  ];

  const handleQuickPost = async () => {
    if (!quickJobData.title || !quickJobData.category) {
      console.warn('Job title and category are required');
      return;
    }

    if (!userProfile?.id) {
      console.error('User profile ID is missing');
      return;
    }

    setIsLoading(prev => ({ ...prev, postingJob: true }));
    setPostingError(null);

    // Create a new job with the data
    const jobData = {
      employer_id: userProfile.id,
      title: quickJobData.title.trim(),
      description: `This is a quick job posting for ${quickJobData.title}. More details will be added later.`,
      category: quickJobData.category,
      budget: quickJobData.budget || 'Negotiable',
      urgency: quickJobData.urgency,
      // For free tier users, set status to draft with moderation_status pending for admin review
      // For paid tiers, set status to published (auto-approved)
      // NOTE: subscription_tier may be null/undefined for free users, so we check for paid tiers explicitly
      status: ['pro', 'premium', 'agency'].includes(userProfile?.subscription_tier) ? 'published' : 'draft',
      moderation_status: ['pro', 'premium', 'agency'].includes(userProfile?.subscription_tier) ? 'approved' : 'pending',
      location: userProfile.location || 'Remote',
      is_remote: true,
      skills_required: []  // Changed from required_skills to skills_required to match database schema
    };
    
    try {
      console.log('Creating job with data:', jobData);
      const result = await employerService.createJob(jobData);
      console.log('Create job result:', result);

      if (result.success) {
        // Pass the created job to the parent component's handler
        if (onPostJob) {
          onPostJob(result.data);
        }
        
        // Reset form and close modal
        setQuickJobData({ title: '', category: '', budget: '', urgency: 'normal' });
        setShowQuickPost(false);
        setPostingError(null);
        
        // Refresh the stats to show the new job
        fetchTodayOverview();
        
        // Show success feedback (optional - could add a toast notification here)
        console.log('Job posted successfully:', result.data);
      } else {
        console.error('Error creating job:', result.error);
        setPostingError(result.error || 'Failed to create job');
      }
    } catch (error) {
      console.error('Failed to create job:', error);
      setPostingError(error.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(prev => ({ ...prev, postingJob: false }));
    }
  };

  const handleRefresh = () => {
    fetchRecentActivities();
  };

  const handleActivityClick = (activity) => {
    // Handle activity click based on type
    switch (activity.type) {
      case 'application':
        // Navigate to application details
        window.location.href = `/employer-dashboard?tab=applications`;
        break;
      case 'message':
        // Open messaging with the sender
        onMessaging(activity.data.conversationId);
        break;
      case 'interview':
        // Open interview details
        onScheduleInterview(activity.data.applicationId);
        break;
      case 'interview_scheduled':
      case 'interview_cancelled':
      case 'interview_rescheduled':
      case 'interview_completed':
        // Navigate to interview management page
        window.location.href = `/interview-management`;
        break;
      case 'hire':
        // Navigate to hired VA details
        window.location.href = `/employer-dashboard?tab=hired`;
        break;
      default:
        // Navigate to activities page for more details
        window.location.href = `/employer-dashboard/activities`;
        break;
    }
  };

  const handleFeedbackSubmit = async (formData) => {
    try {
      const result = await feedbackService.submitFeedback(formData);
      if (result.success) {
        setShowFeedbackForm(false);
        // Optional: Show success notification
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackForm(false);
  };

  return (
    <div className="space-y-6">
      {/* Quick Job Post Modal */}
      {showQuickPost && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4"
          onClick={() => setShowQuickPost(false)}
        >
          <div 
            className="bg-surface rounded-lg border border-border p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Quick Job Post</h3>
              <button
                onClick={() => setShowQuickPost(false)}
                className="text-text-secondary hover:text-text-primary"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Job Title *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Virtual Assistant for Admin Tasks"
                  value={quickJobData.title}
                  onChange={(e) => setQuickJobData({...quickJobData, title: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Category *
                </label>
                <select
                  value={quickJobData.category}
                  onChange={(e) => setQuickJobData({...quickJobData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Select a category</option>
                  {jobCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Budget Range
                </label>
                <Input
                  type="text"
                  placeholder="e.g., $15-25/hour"
                  value={quickJobData.budget}
                  onChange={(e) => setQuickJobData({...quickJobData, budget: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Urgency Level
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {urgencyLevels.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setQuickJobData({...quickJobData, urgency: level.value})}
                      className={`p-2 rounded-lg border text-sm font-medium transition-colors duration-200 ${
                        quickJobData.urgency === level.value
                          ? 'border-primary bg-primary-50 text-primary' :'border-border hover:border-secondary-300'
                      }`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {postingError && (
              <div className="p-3 bg-error-50 border border-error rounded-lg">
                <p className="text-sm text-error">{postingError}</p>
              </div>
            )}
            
            <div className="flex items-center space-x-3 mt-6">
              <Button
                variant="primary"
                onClick={handleQuickPost}
                disabled={!quickJobData.title || !quickJobData.category || isLoading.postingJob}
                isLoading={isLoading.postingJob}
                fullWidth
              >
                {isLoading.postingJob ? 'Posting...' : 'Post Job'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowQuickPost(false);
                  setPostingError(null);
                }}
                disabled={isLoading.postingJob}
                fullWidth
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Today's Overview</h3>
        {isLoading.overview ? (
          <div className="flex justify-center items-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{todayOverview.newApplications}</div>
              <div className="text-xs text-text-secondary">New Applications</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-success">{todayOverview.interviewsScheduled}</div>
              <div className="text-xs text-text-secondary">Interviews Scheduled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-warning">{todayOverview.newMessages}</div>
              <div className="text-xs text-text-secondary">Messages</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-accent">{todayOverview.activeJobs}</div>
              <div className="text-xs text-text-secondary">Active Jobs</div>
            </div>
          </div>
        )}
      </div>
      
      {/* Recent Activity */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Recent Activity</h3>
          <Button variant="ghost" size="sm" iconName="RefreshCw" onClick={handleRefresh} isLoading={isLoading.activities}>
            Refresh
          </Button>
        </div>
        
        {isLoading.activities ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="flex items-start space-x-3 p-3">
                <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : recentActivities.length > 0 ? (
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {recentActivities.slice(0, 6).map((activity) => (
              <button 
                key={activity.id} 
                className="flex items-start space-x-3 p-3 rounded-lg hover:bg-secondary-50 transition-colors duration-200 w-full text-left"
                onClick={() => handleActivityClick(activity)}
              >
                <div className={`w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0`}>
                  <Icon name={activity.icon} size={16} className={activity.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-text-primary">{activity.message}</p>
                  <p className="text-xs text-text-secondary">{activity.time}</p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Inbox" size={32} className="mx-auto text-text-secondary mb-2" />
            <p className="text-text-secondary">No recent activities</p>
          </div>
        )}
        
        {recentActivities.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <Button variant="outline" size="sm" fullWidth onClick={() => window.location.href = '/employer-dashboard/activities'}>
              View All Activity
            </Button>
          </div>
        )}
      </div>

      {/* Core Actions */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Quick Actions</h3>
          <span className="text-xs text-text-secondary bg-primary-50 px-2 py-1 rounded-full">Most Used</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {coreActions.map((item, index) => (
            <button
              key={index}
              onClick={item.action}
              className="p-4 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all duration-200 text-left group relative"
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                  <Icon name={item.icon} size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-text-primary group-hover:text-primary transition-colors duration-200">
                      {item.title}
                    </h4>
                    {item.badge && (
                      <span className="text-xs bg-gradient-to-r from-purple-500 to-blue-500 text-white px-2 py-0.5 rounded-full font-medium">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary mt-1">{item.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Feedback Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-text-primary">Share Your Feedback</h3>
          <Icon name="MessageCircle" size={18} className="text-primary" />
        </div>
        <p className="text-sm text-text-secondary mb-3">
          Help us improve your experience on VA Sprouts
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setShowFeedbackForm(true)}
          className="w-full"
          iconName="Send"
        >
          Give Feedback
        </Button>
      </div>
      

      {/* Management Actions */}
      {/* <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Management Tools</h3>
          <span className="text-xs text-text-secondary bg-secondary-50 px-2 py-1 rounded-full">Advanced</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {managementActions.map((item, index) => (
            <button
              key={index}
              onClick={item.action}
              className="p-4 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all duration-200 text-left group relative"
            >
              <div className="flex items-start space-x-3">
                <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                  <Icon name={item.icon} size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="text-sm font-medium text-text-primary group-hover:text-primary transition-colors duration-200">
                      {item.title}
                    </h4>
                    {item.badge && (
                      <span className="text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full font-medium">
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary mt-1">{item.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div> */}

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <FeedbackForm
          type="general"
          contextId={null}
          contextTitle="Employer Dashboard"
          onSubmit={handleFeedbackSubmit}
          onCancel={handleFeedbackCancel}
        />
      )}
    </div>
  );
};

export default QuickActions;

